import React, {useState, useEffect, useRef} from 'react';
import FilterSelect from "../../../components/filters/filterSelect"
import updateImage from "../../../assets/images/icons/shared/updateImage.svg"
import { getReasonsList, AcceptDeniedOC } from '../../../client/purchaseOrder';
import ModalAlertWarning from '../modals/modalAlertWarning';
import { MODALS } from '../../../config/images';
const RefusedForm = ({ showModalRefused, closeModal, accept, selectedRows}) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [description, setDescription] = useState("");
  const fileInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const MAX_DESCRIPTION_LENGTH = 300;

  const MAX_TOTAL_SIZE_MB = 50; 
  const MAX_TOTAL_SIZE_BYTES = MAX_TOTAL_SIZE_MB * 1024 * 1024;

  const ALLOWED_FILE_TYPES = {
    '.pdf': 'application/pdf',
    '.xls': 'application/vnd.ms-excel',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.csv': 'text/csv',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
    '.svg': 'image/svg+xml; charset=utf-8',
    '.doc': 'application/msword',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.odt': 'application/vnd.oasis.opendocument.text',
    '.ods': 'application/vnd.oasis.opendocument.spreadsheet',
    '.ppt': 'application/vnd.ms-powerpoint',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    '.odp': 'application/vnd.oasis.opendocument.presentation'
  };

  const ALLOWED_EXTENSIONS = Object.keys(ALLOWED_FILE_TYPES);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFileError(null);
    
    if (files.length === 0) {
      e.target.value = '';
      return;
    }

    const totalFiles = selectedFiles.length + files.length;
    if (totalFiles > 2) {
      setFileError(`Solo se permiten máximo 2 archivos.`);
      e.target.value = '';
      return;
    }

    const invalidFiles = files.filter(file => {
      const fileName = file.name.toLowerCase();
      return !ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));
    });
    
    if (invalidFiles.length > 0) {
      const validExtensions = ALLOWED_EXTENSIONS.join(', ');
      setFileError(`Solo se permiten archivos: ${validExtensions}`);
      e.target.value = ''; 
      return;
    }

    const currentTotalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const newTotalSize = files.reduce((sum, file) => sum + file.size, 0);
    const totalSize = currentTotalSize + newTotalSize; 

    if (totalSize > MAX_TOTAL_SIZE_BYTES) {
      setFileError(`El tamaño total de los archivos no debe exceder ${MAX_TOTAL_SIZE_MB} MB.`);
      e.target.value = '';
      return;
    }

    setSelectedFiles(prevFiles => [...prevFiles, ...files]);
    setFileError(null);
    e.target.value = '';
  };

  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const response = await getReasonsList();
        setReasons(response.data);
      } catch (err) {
        setError(err.message || 'Error al cargar los motivos');
      } finally {
        setLoading(false);
      }
    };

    fetchReasons();
  }, []);

useEffect(() => {
  if (!showModalRefused) {
    setSelectedReason("");
    setDescription("");
    setSelectedFiles([]);
    setFileError(null);
    setError(null);
    setShowAlert(false);
    setAlertMessage("");
  }
}, [showModalRefused]);

  const handleDescriptionChange = (e) => {
    const inputText = e.target.value;
    if (inputText.length <= MAX_DESCRIPTION_LENGTH) {
      setDescription(inputText);
    }
  };
  
  const handleSelectReason = (option) => {
    setSelectedReason(option);
  }

  const handleFileButtonClick = () => {
    setFileError(null);
    fileInputRef.current.click();
  };



  const getFileNames = () => {
    if (selectedFiles.length === 0) return "Escoger archivo";
    if (selectedFiles.length === 1) return selectedFiles[0].name;
    return `${selectedFiles.length} archivos seleccionados`;
  };
  const removeFile = (indexToRemove) => {
    setSelectedFiles(prevFiles => 
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
  };


  if (!showModalRefused) return null;

  const reasonOptions = reasons.map(reason => reason.text);

  const handleSubmit = async () => {
     if (isSubmitting) return; 

    if (!selectedReason) {
      setAlertMessage("Debe seleccionar un motivo");
      setShowAlert(true);
      return;
    }
    
    if (!description.trim()) {
      setAlertMessage("Debe ingresar una descripción");
      setShowAlert(true);
      return;
    }
    
    try {
      setIsSubmitting(true);

      const formData = new FormData();
      
      selectedRows.forEach(id => {
        formData.append('purchaseOrderPositionIds', id);
      });

      formData.append('StatusId', 5); // 5 para rechazado
      
      const reason = reasons.find(r => r.text === selectedReason);
      if (reason) {
        formData.append('ReasonId', reason.id);
      }
      
      formData.append('Description', description);

      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await AcceptDeniedOC(formData);

      if (response.status === 200 || response.status === 201) {
        setSelectedReason("");
        setDescription("");
        setSelectedFiles([]);
        accept(); 
      } else {
        throw new Error('Error al rechazar la orden');
      }
    } catch (error) {
      console.error('Error al enviar el rechazo:', error);
      setAlertMessage(error.response?.data?.message || 'Error al enviar el rechazo. Por favor intente nuevamente.');
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center w-full bg-black bg-opacity-50">
      <div className="lg:w-[28%] w-[85%] h-auto bg-white rounded-3xl shadow-lg flex flex-col pb-4  ">
        <div className='flex justify-end'>
          <button
            onClick={closeModal}
            className="px-4 py-2 rounded-md"
          >
            <img src={MODALS.CLOSE} alt="cerrar"/>
          </button>
        </div>
        <div className='flex flex-col justify-center gap-3 px-8 pb-2'>
            <div className='flex flex-col w-full gap-3'>
                <div className='flex flex-col gap-2'>
                    <p className="text-sm font-itcdemi">
                        Seleccione el motivo: <span className="text-red">*</span>
                    </p>
                    <FilterSelect
                    width='w-full'
                    text="Escoger motivo"
                    options ={reasonOptions}
                    onSelect={handleSelectReason}
                    selectedOption={selectedReason}
                    />
                </div>

                <div className='flex flex-col gap-2'>
                    <p className="text-sm font-itcdemi">
                        Subir archivos (máximo 2):
                    </p>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                      multiple
                      accept={ALLOWED_EXTENSIONS.join(',')}
                    />
                    <button 
                      type="button"
                      onClick={handleFileButtonClick}
                      className="flex items-center h-[40px] justify-between border-[1px] border-[#D1D5DB] rounded-md font-itcbook text-[1vw] pl-3 focus:outline-none"
                    >
                      <p className="text-[#6B7280] text-sm font-itcbook overflow-hidden whitespace-nowrap text-ellipsis px-1">
                        {getFileNames()}
                      </p>
                      <div className="flex items-center justify-center h-full border-l border-[#D1D5DB] px-3 ml-3">
                        <img src={updateImage} className='w-[13px]' alt="Subir"/>
                      </div>
                    </button>
                    {fileError && (
                      <p className="text-xs text-red font-itcbook">{fileError}</p>
                    )}
                    
                    {selectedFiles.length > 0 && (
                      <div className="overflow-y-auto text-sm text-gray max-h-20">
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between ">
                            <span className="flex-1 text-xs truncate font-itcbook">
                              • {file.name}
                            </span>
                            <button 
                              onClick={() => removeFile(index)}
                              className="ml-2 text-red"
                              type="button"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                </div>

                <div className='flex flex-col gap-2'>
                  <div className='flex items-center justify-between'>
                    <p className="text-sm font-itcdemi">
                      Describa: <span className="text-red">*</span>
                    </p>
                    <span className={`text-xs ${description.length >= MAX_DESCRIPTION_LENGTH ? 'text-red-500' : 'text-gray-500'}`}>
                      {description.length}/{MAX_DESCRIPTION_LENGTH}
                    </span>
                  </div>

                  <textarea
                  placeholder='Describa'
                  value={description}
                  onChange={handleDescriptionChange}
                  className='p-2 h-auto focus:outline-none border-[#D1D5DB]  placeholder:text-[#6B7280] rounded-md border-[1px] font-itcbook text-sm'
                  />
                  {description.length >= MAX_DESCRIPTION_LENGTH && (
                    <p className="text-xs text-red font-itcbook">Has alcanzado el límite máximo de caracteres</p>
                  )}
                </div>
            </div>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`font-itcbook text-sm underline ${isSubmitting ? 'text-gray-400' : 'text-blue'}`}
              >
                {isSubmitting ? 'Enviando...' : 'Aceptar'}
            </button>
        </div>
      </div>
      <ModalAlertWarning 
        showModalAlertWarning={showAlert}
        closeModal={() => setShowAlert(false)}
        error={alertMessage}
      />
    </div>
  );
};
export default RefusedForm;